import React, { useEffect, useState } from 'react';
import { createStyles, Theme } from '@mui/material/styles';
import { withStyles } from '@mui/styles';
import TreeItem, { TreeItemProps } from '@mui/lab/TreeItem';
import { TransitionProps } from '@mui/material/transitions';
import { animated, useSpring } from 'react-spring';
import Collapse from '@mui/material/Collapse';
import { getPlatform } from '../../../utils/getCurrentOS';
import mainClasses from '../TreeAccessView/treeAccessView.module.scss';
import style from '../../DnDTree/DnDTree.module.scss';
import {
  AccessTree,
  CHECKBOXTYPES,
  ENTITYTYPES,
  IAccess,
} from '../../../types/accessTree';

const StyledTreeItem = withStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    iconContainer: {
      marginBottom: 20,
      '& .close': {
        opacity: 0.3,
      },
    },
    group: {
      marginLeft: 7,
      paddingLeft: 18,
    },
    label: {
      marginBottom: 20,
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 16,
      width: '100%',
      fontWeight: 400,
      color: '#444A52',
    },
  }),
)((props: TreeItemProps) => (
  <TreeItem {...props} TransitionComponent={TransitionComponent} />
));

function TransitionComponent(props: TransitionProps) {
  const style = useSpring({
    from: { opacity: 0, transform: 'translate3d(20px,0,0)' },
    to: {
      opacity: props.in ? 1 : 0,
      transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
    },
  });

  return (
    <animated.div style={style}>
      <Collapse {...props} />
    </animated.div>
  );
}

type nodeProps = {
  allTree: AccessTree;
  currentNode: AccessTree;
  parentNode?: AccessTree;
  setParentNode?: (data: AccessTree) => void;
  setCurrentNode?: (data: AccessTree) => void;
  handleAllTreeById: (id: number) => void;
  isShowChildren: boolean;
  handleRequestData: (item: IAccess, itemType: ENTITYTYPES) => void;
  updateAccess: () => void;
  searchNodeInTreeById: (id: number) => AccessTree;
  handleExpandedArray: (id: number) => void;
  parentCheckboxStyle?: CHECKBOXTYPES | null;
  clearParentCheckboxStyle?: () => void;
};

export const Node = ({
  allTree,
  currentNode,
  parentNode,
  setParentNode,
  handleAllTreeById,
  searchNodeInTreeById,
  setCurrentNode,
  isShowChildren,
  handleRequestData,
  updateAccess,
  handleExpandedArray,
  parentCheckboxStyle,
  clearParentCheckboxStyle,
}: nodeProps) => {
  const [currentNodeState, setCurrentNodeState] = useState<AccessTree>(
    currentNode,
  );

  const parentRecursion = (isStarter: boolean) => {
    if (!!parentNode && !isStarter) {
      const checkedArr: number[] = [];
      const indeterminateCheckedArr = [];
      parentNode.children.forEach((item) => {
        if (item.checkboxType === CHECKBOXTYPES.CHECKBOX_CHECKED) {
          checkedArr.push(item.id);
        } else if (item.checkboxType === CHECKBOXTYPES.CHECKBOX_INDETERMINATE) {
          indeterminateCheckedArr.push(item.id);
        }
      });
      if (checkedArr.length === 0) {
        if (indeterminateCheckedArr.length !== 0) {
          updateParentCheckboxType(CHECKBOXTYPES.CHECKBOX_INDETERMINATE);
        } else {
          updateParentCheckboxType(CHECKBOXTYPES.CHECKBOX_UNCHECKED);
        }
      } else if (checkedArr.length === parentNode.children.length) {
        updateParentCheckboxType(CHECKBOXTYPES.CHECKBOX_CHECKED);
      } else {
        updateParentCheckboxType(CHECKBOXTYPES.CHECKBOX_INDETERMINATE);
      }
    }
  };

  const updateParentCheckboxType = (checkboxType: number) => {
    if (parentNode && setParentNode) {
      setParentNode({
        ...parentNode,
        checkboxType,
      });
    }
  };

  const [checkboxStyle, setCheckboxStyle] = useState<CHECKBOXTYPES>(
    currentNodeState?.checkboxType,
  );

  const [
    manualCheckboxStyle,
    setManualCheckboxStyle,
  ] = useState<CHECKBOXTYPES | null>(null);

  const clearManualCheckboxStyle = () => {
    setManualCheckboxStyle(null);
  };

  const handleCheckboxStyle = (checkboxType: number) => {
    setCheckboxStyle(checkboxType);
  };

  const [isShowChildrenState, setIsShowChildrenState] = useState(
    isShowChildren,
  );

  const handleNodeAccess = () => {
    const modifiedCheckbox =
      checkboxStyle === CHECKBOXTYPES.CHECKBOX_UNCHECKED ||
      checkboxStyle === CHECKBOXTYPES.CHECKBOX_INDETERMINATE
        ? CHECKBOXTYPES.CHECKBOX_CHECKED
        : CHECKBOXTYPES.CHECKBOX_UNCHECKED;

    handleCheckboxStyle(modifiedCheckbox);
    setManualCheckboxStyle(modifiedCheckbox);

    if (clearParentCheckboxStyle) {
      clearParentCheckboxStyle();
    }

    if (parentNode) {
      const newParentNode = {
        ...parentNode,
        children: parentNode.children.map((item) => ({
          ...item,
          checkboxType:
            item.id === currentNodeState.id
              ? modifiedCheckbox
              : item.checkboxType,
        })),
      };

      if (setParentNode) {
        setParentNode(newParentNode);
      }
    }

    handleRequestData(
      {
        id: currentNodeState.id,
        access:
          checkboxStyle === CHECKBOXTYPES.CHECKBOX_UNCHECKED ||
          checkboxStyle === CHECKBOXTYPES.CHECKBOX_INDETERMINATE,
      },
      currentNodeState.entityType,
    );

    updateAccess();
  };

  useEffect(() => {
    parentRecursion(false);
    setCurrentNodeState({
      ...currentNodeState,
      children: currentNodeState.children.map((item) => ({
        ...item,
        checkboxType:
          checkboxStyle === CHECKBOXTYPES.CHECKBOX_INDETERMINATE
            ? item.checkboxType
            : checkboxStyle,
      })),
    });
  }, [checkboxStyle]);

  useEffect(() => {
    if (checkboxStyle !== currentNodeState?.checkboxType) {
      setCheckboxStyle(currentNodeState?.checkboxType);
    }

    if (parentNode) {
      const newParentNode = {
        ...parentNode,
        children: parentNode.children.map((item) => ({
          ...item,
          checkboxType:
            item.id === currentNodeState.id
              ? currentNodeState?.checkboxType
              : item.checkboxType,
        })),
      };

      if (setParentNode) {
        setParentNode(newParentNode);
      }
    }
  }, [currentNodeState?.checkboxType]);

  useEffect(() => {
    if (
      parentCheckboxStyle !== null &&
      parentCheckboxStyle !== undefined &&
      parentCheckboxStyle !== CHECKBOXTYPES.CHECKBOX_INDETERMINATE
    ) {
      setCheckboxStyle(parentCheckboxStyle);
      setManualCheckboxStyle(parentCheckboxStyle);
    }
  }, [parentCheckboxStyle]);

  useEffect(() => {
    if (manualCheckboxStyle === null && clearParentCheckboxStyle) {
      clearParentCheckboxStyle();
    }
  }, [manualCheckboxStyle]);

  return (
    <>
      <div className={style.row}>
        <StyledTreeItem
          nodeId={currentNodeState.id.toString()}
          onClick={() => {
            handleExpandedArray(currentNodeState.id);
            setIsShowChildrenState(!isShowChildrenState);
          }}
          label={
            <div className={style.label2} style={{ paddingRight: 24 }}>
              <div className={style.label}>
                <div>{currentNodeState.name}</div>
              </div>
            </div>
          }
        >
          <div>
            {currentNodeState.children?.length
              ? currentNodeState.children.map((item, index) => {
                  return (
                    <Node
                      allTree={allTree}
                      currentNode={item}
                      handleAllTreeById={handleAllTreeById}
                      setParentNode={setCurrentNodeState}
                      parentNode={currentNodeState}
                      isShowChildren={false}
                      handleRequestData={handleRequestData}
                      updateAccess={updateAccess}
                      searchNodeInTreeById={searchNodeInTreeById}
                      handleExpandedArray={handleExpandedArray}
                      parentCheckboxStyle={manualCheckboxStyle}
                      clearParentCheckboxStyle={clearManualCheckboxStyle}
                    />
                  );
                })
              : null}
          </div>
        </StyledTreeItem>
        <div
          className={`
              ${
                getPlatform() === 'Macintosh'
                  ? mainClasses.mac
                  : mainClasses.windows
              }
              ${
                checkboxStyle === CHECKBOXTYPES.CHECKBOX_INDETERMINATE
                  ? mainClasses.indeterminate
                  : mainClasses.checkbox
              }
              `}
        >
          <input
            type="checkbox"
            id={`id-${currentNode.id}`}
            checked={checkboxStyle === CHECKBOXTYPES.CHECKBOX_CHECKED}
            onChange={() => {
              handleNodeAccess();
            }}
          />
          <label htmlFor={`id-${currentNode.id}`}>
            <span />
          </label>
        </div>
      </div>
    </>
  );
};
